import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Storefront as StorefrontIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import CountUp from "react-countup";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import api from "../../../services/api";

const DEFAULT_STATS = {
  totalCompanies: 0,
  totalBrands: 0,
  totalStores: 0,
  totalUsers: 0,
  recentCompanies: [],
  recentBrands: [],
  systemHealth: {
    apiResponseTime: 150,
    errorRate: 0.5,
    activeSessions: 42,
    uptime: 99.9,
  },
  salesTrend: [],
  alertStores: [],
};

const STAT_CARDS_CONFIG = [
  {
    id: "companies",
    title: "運営会社数",
    getValue: (stats) => stats?.totalCompanies || 0,
    icon: BusinessIcon,
    color: "#1976d2",
    gradient: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
  },
  {
    id: "brands",
    title: "ブランド数",
    getValue: (stats) => stats?.totalBrands || 0,
    icon: StorefrontIcon,
    color: "#2e7d32",
    gradient: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)",
  },
  {
    id: "stores",
    title: "店舗数",
    getValue: (stats) => stats?.totalStores || 0,
    icon: StoreIcon,
    color: "#ed6c02",
    gradient: "linear-gradient(135deg, #ed6c02 0%, #ffb74d 100%)",
  },
  {
    id: "users",
    title: "ユーザー数",
    getValue: (stats) => stats?.totalUsers || 0,
    icon: PeopleIcon,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
  },
];

// eslint-disable-next-line no-unused-vars
const HEALTH_COLORS = ["#4caf50", "#ff9800", "#f44336"];

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetchStats();

    // 1分ごとにデータを更新
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      setError(null);
      const response = await api.get("/api/system/dashboard/stats");

      if (!response.data) {
        throw new Error("データが取得できませんでした");
      }

      setStats({
        ...DEFAULT_STATS,
        ...response.data,
        recentCompanies: Array.isArray(response.data?.recentCompanies)
          ? response.data.recentCompanies
          : [],
        recentBrands: Array.isArray(response.data?.recentBrands)
          ? response.data.recentBrands
          : [],
        systemHealth: response.data?.systemHealth || DEFAULT_STATS.systemHealth,
        salesTrend: response.data?.salesTrend || [],
        alertStores: response.data?.alertStores || [],
      });
    } catch (error) {
      console.error("統計情報の取得に失敗しました:", error);
      setError(error.response?.data?.message || "データの取得に失敗しました");
      setStats(DEFAULT_STATS);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = (value, thresholds) => {
    if (value <= thresholds[0]) return { color: "#4caf50", label: "良好" };
    if (value <= thresholds[1]) return { color: "#ff9800", label: "注意" };
    return { color: "#f44336", label: "警告" };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const healthData = [
    { name: "正常", value: 85 },
    { name: "注意", value: 10 },
    { name: "警告", value: 5 },
  ];

  return (
    <Fade in={mounted} timeout={500}>
      <Box>
        {/* ヘッダー */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              システム管理ダッシュボード
            </Typography>
            <Typography variant="body2" color="text.secondary">
              最終更新: {new Date().toLocaleString("ja-JP")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="データ更新">
              <IconButton onClick={fetchStats} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 統計カード */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {STAT_CARDS_CONFIG.map((card) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={card.id}>
                <Card
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 24px ${alpha(card.color, 0.25)}`,
                    },
                  }}
                  onClick={() => navigate(`/system-admin/${card.id === "companies" ? "companies" : card.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {card.title}
                        </Typography>
                        <Typography variant="h3" fontWeight={700} sx={{ color: card.color }}>
                          <CountUp
                            end={card.getValue(stats)}
                            duration={1.5}
                            separator=","
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: card.gradient,
                        }}
                      >
                        <Icon sx={{ fontSize: 32, color: "#fff" }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Grid container spacing={3}>
          {/* システムヘルスモニター */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="システムヘルス"
                avatar={<SpeedIcon color="primary" />}
                action={
                  <Chip
                    label="正常稼働中"
                    color="success"
                    size="small"
                    icon={<CheckCircleIcon />}
                  />
                }
              />
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">API応答時間</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.systemHealth?.apiResponseTime || 150}ms
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((stats.systemHealth?.apiResponseTime || 150) / 5, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        bgcolor: getHealthStatus(stats.systemHealth?.apiResponseTime || 150, [100, 300]).color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">エラー率</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.systemHealth?.errorRate || 0.5}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.systemHealth?.errorRate || 0.5) * 10}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        bgcolor: getHealthStatus((stats.systemHealth?.errorRate || 0.5), [1, 5]).color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">アクティブセッション</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      <CountUp end={stats.systemHealth?.activeSessions || 42} duration={1} />
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h2" fontWeight={700} color="success.main">
                    {stats.systemHealth?.uptime || 99.9}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    稼働率（過去30日間）
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 注意が必要な店舗 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="注意が必要な店舗"
                avatar={<WarningIcon color="warning" />}
                action={
                  <Chip
                    label={`${stats.alertStores?.length || 0}件`}
                    color="warning"
                    size="small"
                  />
                }
              />
              <CardContent>
                {stats.alertStores && stats.alertStores.length > 0 ? (
                  <List disablePadding>
                    {stats.alertStores.map((store, index) => (
                      <React.Fragment key={store.id}>
                        <ListItem
                          sx={{
                            bgcolor: alpha(
                              store.severity === "error"
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                              0.1
                            ),
                            borderRadius: 2,
                            mb: 1,
                          }}
                        >
                          <ListItemIcon>
                            {store.severity === "error" ? (
                              <ErrorIcon color="error" />
                            ) : (
                              <WarningIcon color="warning" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={store.name}
                            secondary={store.issue}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                          <Button size="small" variant="outlined">
                            確認
                          </Button>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
                    <Typography color="text.secondary">
                      注意が必要な店舗はありません
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* 売上トレンド */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="週間売上トレンド"
                avatar={<TimelineIcon color="primary" />}
              />
              <CardContent>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.salesTrend}>
                      <defs>
                        <linearGradient id="colorSalesTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <RechartsTooltip
                        formatter={(value) => [`¥${value.toLocaleString()}`, "売上"]}
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="sales"
                        stroke={theme.palette.primary.main}
                        fillOpacity={1}
                        fill="url(#colorSalesTrend)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 最近の登録企業 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="最近の登録企業"
                action={
                  <Button
                    size="small"
                    endIcon={<TrendingUpIcon />}
                    onClick={() => navigate("/system-admin/companies")}
                  >
                    すべて表示
                  </Button>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>会社名</TableCell>
                        <TableCell>契約プラン</TableCell>
                        <TableCell>登録日</TableCell>
                        <TableCell>ステータス</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(stats?.recentCompanies) &&
                        stats.recentCompanies.map((company) => (
                          <TableRow
                            key={company.id || company._id}
                            sx={{
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                            }}
                          >
                            <TableCell>{company.name}</TableCell>
                            <TableCell>
                              <Chip label={company.plan} size="small" />
                            </TableCell>
                            <TableCell>
                              {new Date(company.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={company.status}
                                size="small"
                                color={company.status === "active" ? "success" : "default"}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      {(!stats?.recentCompanies || stats.recentCompanies.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            データがありません
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* 最近のブランド */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="最近のブランド"
                action={
                  <Button
                    size="small"
                    endIcon={<TrendingUpIcon />}
                    onClick={() => navigate("/system-admin/brands")}
                  >
                    すべて表示
                  </Button>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ブランド名</TableCell>
                        <TableCell>会社名</TableCell>
                        <TableCell>登録日</TableCell>
                        <TableCell>ステータス</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(stats?.recentBrands) &&
                        stats.recentBrands.map((brand) => (
                          <TableRow
                            key={brand.id || brand._id}
                            sx={{
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                            }}
                          >
                            <TableCell>{brand.name}</TableCell>
                            <TableCell>{brand.companyName}</TableCell>
                            <TableCell>
                              {new Date(brand.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={brand.status}
                                size="small"
                                color={brand.status === "active" ? "success" : "default"}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      {(!stats?.recentBrands || stats.recentBrands.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            データがありません
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default Dashboard;
